import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar, Label, Popover, PopoverContent, PopoverTrigger } from '@zawarski/eti-ui';
import { longDateFormat, parseDate } from '@utils/index';

/**
 * eti-ui's DatePicker for a field low in a dialog.
 *
 * eti-ui opens its calendar downward only (collision avoidance off), so a date field near the
 * bottom of a dialog drops the calendar off the screen. This one opens below when there is room
 * and flips above when there is not. The trigger repeats eti-ui's outline button through its
 * own classes, which eti-ui's stylesheet already carries; it is a native button so Radix can
 * anchor the calendar to it.
 */
const DialogDatePicker = ({
  label,
  value,
  placeholder = 'Pick a date',
  onChange,
}: {
  label?: string;
  /** A Date or a 'YYYY-MM-DD' string; empty shows the placeholder. */
  value?: string | Date | null;
  placeholder?: string;
  onChange: (date: Date) => void;
}) => {
  const [open, setOpen] = useState(false);
  const date = parseDate(value) ?? undefined;
  return (
    <div className='eu:grid eu:w-full eu:items-center eu:gap-2 eu:relative'>
      {label ? (
        <Label className='eu:flex eu:gap-0.5 eu:items-center eu:w-full eu:p-0 eu:m-0 eu:leading-none eu:h-fit'>
          {label}
          <span className='eu:w-2' />
        </Label>
      ) : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type='button'
            data-empty={!date}
            className='eu:inline-flex eu:gap-2 eu:whitespace-nowrap eu:rounded-md eu:text-sm eu:transition-all eu:shrink-0 eu:outline-none eu:focus-visible:border-ring eu:focus-visible:ring-ring/50 eu:focus-visible:ring-[3px] eu:cursor-pointer eu:border eu:border-border eu:bg-background eu:text-foreground eu:shadow-xs eu:hover:bg-accent eu:hover:text-accent-foreground eu:h-9 eu:px-4 eu:py-2 eu:data-[empty=true]:text-muted-foreground eu:w-full eu:justify-between eu:text-left eu:font-normal eu:flex eu:items-center'>
            <span className='eu:flex eu:items-center eu:gap-2'>
              {date ? longDateFormat(date) : <span>{placeholder}</span>}
            </span>
            <CalendarIcon className='eu:size-4 eu:shrink-0' />
          </button>
        </PopoverTrigger>
        <PopoverContent className='eu:w-auto eu:p-0' side='bottom' avoidCollisions>
          <Calendar
            mode='single'
            selected={date}
            defaultMonth={date}
            onSelect={(picked) => {
              if (!picked) return;
              onChange(picked);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DialogDatePicker;
